import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Tooltip from '@mui/material/Tooltip'
import {
	type ComponentProps,
	type PropsWithChildren,
	type ReactNode,
	type RefObject,
	type SubmitEventHandler,
	useRef,
	useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { Navigation } from '../features/Navigation.tsx'
import { HelpButton } from '../Intercom.tsx'
import type { I18nKey } from '../i18n/i18n.ts'
import { ids } from '../ids.ts'
import { Box } from './Box.tsx'
import { Button, CircularProgress, Collapse, Divider, Grid, Paper, Step, StepContent, StepLabel, Stepper, Typography } from './generic.tsx'
import { I18n } from './I18n.tsx'
import { Icon, type IconName } from './Icon.tsx'
import { IconButton } from './IconButton.tsx'
import { Stack } from './Stack.tsx'

export const WithNavigation = ({ children, topBar }: { children?: ReactNode; topBar?: ReactNode }) => (
	<Stack full col>
		<Stack sx={{ p: 2 }}>
			<Stack>
				<Navigation />
			</Stack>
			{topBar}
			<Stack sx={{ ml: 'auto' }}>
				<HelpButton size='large' />
			</Stack>
		</Stack>
		<Stack full col sx={{ flexGrow: 1, overflow: 'auto', position: 'relative' }}>
			{children}
		</Stack>
	</Stack>
)

export const WithoutNavigation = ({
	children,
	topBar,
	onBack,
	hideBorder,
}: {
	children?: ReactNode
	topBar: ReactNode
	onBack: () => void
	hideBorder?: boolean
}) => {
	const { t } = useTranslation()
	return (
		<Stack full col spacing={0}>
			<Stack
				spacing={0}
				sx={{
					h: 56,
					pr: 2,
					align: 'center',
					color: (t) => t.palette.text.secondary,
					borderBottom: hideBorder ? 'none' : 1,
					borderColor: (t) => t.palette.divider,
					bgcolor: (t) => t.palette.background.paper1,
				}}
			>
				<IconButton data-ta={ids.common.topBarBackBtn} icon='ArrowLeft' label={t(($) => $.goBack)} size='large' onClick={onBack} />
				<Divider variant='middle' orientation='vertical' flexItem sx={{ mr: 2 }} />
				{topBar}
				<Stack sx={{ ml: 'auto', pl: 2 }}>
					<HelpButton size='small' />
				</Stack>
			</Stack>
			<Stack full col sx={{ flexGrow: 1, overflow: 'auto', position: 'relative' }}>
				{children}
			</Stack>
		</Stack>
	)
}

export const FormLayout = ({
	steps,
	activeStep,
	disableNext,
	isSubmitting,
	showStepLabelIndex,
	stepsControl,
	title,
	onBack,
	children,
	nextButtonText,
	saveButtonText,
}: {
	steps: { label: ReactNode; description: ReactNode; id: string }[]
	showStepLabelIndex?: boolean
	activeStep: number
	disableNext?: boolean
	isSubmitting?: boolean
	stepsControl?: {
		onNext?: () => void
		onBack?: () => void
		onSave: SubmitEventHandler<HTMLFormElement>
		onDelete?: () => void
		onCancel: () => void
		disableDelete?: boolean
	}
	title: I18nKey
	onBack: () => void
	children?: ReactNode
	nextButtonText?: string
	saveButtonText?: string
}) => (
	<BaseFormLayout
		onBack={onBack}
		onSubmit={stepsControl?.onSave}
		topBar={<FormLayout.TopBarTitle data-ta={ids.formLayout.heading} i18nKey={title} />}
		sideBar={
			<Stepper orientation='vertical' activeStep={activeStep} sx={{ minW: 240, w: 240, py: 2, pl: 0, pr: 1 }}>
				{steps.map(({ label, description, id }, index) => (
					<Step data-ta={ids.formLayout.step(index + 1)} key={id}>
						<StepLabel>
							{showStepLabelIndex && `${index + 1}. `}
							{label}
						</StepLabel>
						<StepContent>{description}</StepContent>
					</Step>
				))}
			</Stepper>
		}
		bottomBar={
			<Stack spacing={0.5}>
				{stepsControl?.onDelete && (
					<Button
						data-ta={ids.formLayout.deleteBtn}
						variant='text'
						color='error'
						onClick={stepsControl.onDelete}
						disabled={stepsControl.disableDelete}
						startIcon={<Icon name='Trash' />}
					>
						<I18n i18nKey={($) => $.delete} />
					</Button>
				)}
				<Button data-ta={ids.formLayout.cancelBtn} variant='text' onClick={stepsControl?.onCancel}>
					<I18n i18nKey={($) => $.cancel} />
				</Button>
				{steps.length > 1 && (
					<Button data-ta={ids.formLayout.backBtn} color='secondary' onClick={stepsControl?.onBack} disabled={activeStep === 0}>
						<I18n i18nKey={($) => $.back} />
					</Button>
				)}
				{steps.length > 1 && activeStep !== steps.length - 1 && (
					<Button data-ta={ids.formLayout.nextBtn} onClick={stepsControl?.onNext} disabled={disableNext}>
						{nextButtonText ?? <I18n i18nKey={($) => $.next} />}
					</Button>
				)}
				{activeStep === steps.length - 1 && (
					<Button data-ta={ids.formLayout.saveBtn} type='submit' disabled={isSubmitting}>
						{isSubmitting ? (
							<Stack center>
								<CircularProgress size={16} />
								{saveButtonText ?? <I18n i18nKey={($) => $.save} />}
							</Stack>
						) : (
							(saveButtonText ?? <I18n i18nKey={($) => $.save} />)
						)}
					</Button>
				)}
			</Stack>
		}
	>
		{children}
	</BaseFormLayout>
)

export const BaseFormLayout = ({
	children,
	topBar,
	bottomBar,
	sideBar,
	onBack,
	onSubmit,
}: {
	topBar: ReactNode
	bottomBar?: ReactNode
	sideBar?: ReactNode
	onBack: () => void
	onSubmit?: SubmitEventHandler<HTMLFormElement>
	children?: ReactNode
}) => (
	<WithoutNavigation topBar={topBar} onBack={onBack} hideBorder>
		<Stack spacing={0} sx={{ pt: 2, px: 2, h: '100%', bgcolor: (t) => t.palette.background.paper1 }}>
			{sideBar}
			<Box
				component='form'
				noValidate
				onSubmit={(event) => {
					event.preventDefault()
					onSubmit?.(event)
				}}
				flex
				col
				sx={{ flexGrow: 1, pb: 2 }}
			>
				<Box sx={{ display: 'grid', gridTemplateRows: '1fr auto', overflow: 'auto', gap: 2, h: '100%' }}>
					<Paper variant='outlined' sx={{ w: '100%', h: '100%', overflow: 'auto' }}>
						{children}
					</Paper>
					{bottomBar}
				</Box>
			</Box>
		</Stack>
	</WithoutNavigation>
)

FormLayout.Split = ({ left, right }: { left: ReactNode; right: ReactNode }) => (
	<Grid container sx={{ h: '100%' }}>
		<Grid size={5} sx={{ maxWidth: 640, borderRight: 1, borderColor: (t) => t.palette.divider, h: '100%' }}>
			{left}
		</Grid>
		<Grid size='grow' height='100%'>
			{right}
		</Grid>
	</Grid>
)

FormLayout.TopBarTitle = (props: PropsWithChildren<{ i18nKey?: I18nKey; 'data-ta'?: string }>) => (
	<Typography data-ta={props['data-ta']} variant='h1' noWrap>
		{props.i18nKey && <I18n i18nKey={props.i18nKey} />}
		{props.children}
	</Typography>
)

type PanelControl<TValue extends string> = { value: TValue; icon: IconName; label: string }

export type PanelControls<TValue extends string> = {
	options: readonly PanelControl<TValue>[]
	value: TValue
	onChange: (value: TValue) => void
	getDataTa?: (value: TValue) => string
	getRailDataTa?: (value: TValue) => string
}

const PanelControlsToggle = <TValue extends string>({ options, value, onChange, getDataTa }: PanelControls<TValue>) => (
	<ToggleButtonGroup exclusive>
		{options.map((option) => (
			<ToggleButton
				key={option.value}
				data-ta={getDataTa?.(option.value)}
				value={option.value}
				selected={value === option.value}
				onChange={() => onChange(option.value)}
				sx={{ gap: 0.5 }}
			>
				<Icon name={option.icon} size='xsmall' />
				{option.label}
			</ToggleButton>
		))}
	</ToggleButtonGroup>
)

const railCellSx: ComponentProps<typeof Stack>['sx'] = {
	w: '100%',
	h: 64,
	borderRadius: 0,
	border: 0,
	borderBottom: 1,
	borderColor: (t) => t.palette.dividerSubtle,
}

const CollapsedPanelRail = <TValue extends string>({
	onExpand,
	expandButtonRef,
	controls,
}: {
	onExpand: () => void
	expandButtonRef: RefObject<HTMLButtonElement | null>
	controls?: PanelControls<TValue>
}) => {
	const { t } = useTranslation()
	return (
		<Stack
			col
			spacing={0}
			sx={{
				w: 48,
				h: '100%',
				flexShrink: 0,
				bgcolor: (t) => t.palette.background.paper1,
				borderRight: 1,
				borderColor: (t) => t.palette.divider,
			}}
		>
			<IconButton
				ref={expandButtonRef}
				data-ta={ids.formLayout.expandPanelBtn}
				icon='Sidebar'
				label={t(($) => $.expandPanel)}
				sx={railCellSx}
				onClick={onExpand}
			/>
			{controls && (
				<ToggleButtonGroup orientation='vertical' exclusive>
					{controls.options.map((option) => (
						<Tooltip key={option.value} title={option.label} placement='right' disableInteractive>
							<ToggleButton
								data-ta={controls.getRailDataTa?.(option.value)}
								value={option.value}
								selected={controls.value === option.value}
								aria-label={option.label}
								sx={railCellSx}
								onChange={() => controls.onChange(option.value)}
							>
								<Icon name={option.icon} />
							</ToggleButton>
						</Tooltip>
					))}
				</ToggleButtonGroup>
			)}
		</Stack>
	)
}

const PANEL_WIDTH = 'min(420px, 40vw)'
const PANEL_TRANSITION_MS = 300

export const CollapsiblePanelLayout = <TValue extends string>({
	panel,
	children,
	controls,
	collapsible,
}: {
	panel: (collapseButton: ReactNode) => ReactNode
	children: ReactNode
	controls?: PanelControls<TValue>
	collapsible?: boolean
}) => {
	const { t } = useTranslation()
	const [isCollapsed, setIsCollapsed] = useState(false)
	const collapseButtonRef = useRef<HTMLButtonElement>(null)
	const expandButtonRef = useRef<HTMLButtonElement>(null)

	return (
		<Stack full gap={0} sx={{ overflow: 'hidden' }}>
			{collapsible && (
				<Collapse
					in={isCollapsed}
					orientation='horizontal'
					timeout={PANEL_TRANSITION_MS}
					sx={{ flexShrink: 0 }}
					onEntering={() => expandButtonRef.current?.focus({ preventScroll: true })}
				>
					<CollapsedPanelRail controls={controls} expandButtonRef={expandButtonRef} onExpand={() => setIsCollapsed(false)} />
				</Collapse>
			)}
			<Collapse
				in={!isCollapsed}
				orientation='horizontal'
				timeout={PANEL_TRANSITION_MS}
				sx={{ flexShrink: 0 }}
				onEntering={() => collapseButtonRef.current?.focus({ preventScroll: true })}
			>
				<Box sx={{ w: PANEL_WIDTH, h: '100%', borderRight: 1, borderColor: 'divider', bgcolor: (t) => t.palette.background.paper }}>
					{panel(
						collapsible && (
							<IconButton
								ref={collapseButtonRef}
								data-ta={ids.formLayout.collapsePanelBtn}
								icon='Sidebar'
								label={t(($) => $.collapsePanel)}
								size='small'
								onClick={() => setIsCollapsed(true)}
							/>
						),
					)}
				</Box>
			</Collapse>
			<Box sx={{ flexGrow: 1, minW: 0 }}>{children}</Box>
		</Stack>
	)
}

export const FormPage = <TValue extends string = string>({
	headerContent,
	headerAction,
	dividerControls,
	children,
	dataTa,
}: {
	headerContent?: ReactNode
	headerAction?: ReactNode
	dividerControls?: PanelControls<TValue>
	children: ReactNode
	dataTa?: string
}) => (
	<Stack col sx={{ h: '100%' }} data-ta={dataTa}>
		{(headerContent || dividerControls) && (
			<Box sx={{ position: 'relative', mb: dividerControls ? 2 : 0 }}>
				<Stack
					col
					spacing={2}
					sx={{
						p: 2,
						pr: headerAction ? 7 : 2,
						pb: dividerControls ? 4 : 2,
						flexShrink: 0,
						backgroundColor: (t) => t.palette.background.paper1,
					}}
				>
					{headerContent}
				</Stack>
				{headerAction && <Box sx={{ position: 'absolute', top: 12, right: 16 }}>{headerAction}</Box>}
				<Divider />
				{dividerControls && (
					<Box sx={{ position: 'absolute', left: '50%', bottom: 0, transform: 'translate(-50%, 50%)' }}>
						<PanelControlsToggle {...dividerControls} />
					</Box>
				)}
			</Box>
		)}
		{children}
	</Stack>
)

FormPage.Content = ({ children, sx }: { children: ReactNode; sx?: ComponentProps<typeof Stack>['sx'] }) => {
	return (
		<Stack col spacing={2} sx={{ p: 2, minHeight: 0, width: '100%', overflow: 'auto', flexGrow: 1, ...sx }}>
			{children}
		</Stack>
	)
}
